import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { HardHat, Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bem-vindo, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/ponto", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Brand side */}
      <div
        className="relative lg:w-1/2 min-h-[240px] lg:min-h-screen flex items-end p-8 lg:p-12 overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(11,19,43,0.55) 0%, rgba(11,19,43,0.95) 100%), url('https://images.unsplash.com/photo-1759922378219-1d31edb644f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <HardHat className="w-7 h-7 text-primary-foreground" />
            </div>
            <span className="font-display text-2xl font-extrabold tracking-tight text-white">
              Monumental Time
            </span>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-white leading-tight max-w-md">
            Gestão de tempo e obras para a construção civil.
          </h1>
          <p className="text-slate-300 mt-3 max-w-md">
            Registo de ponto, obras, clientes, faltas e relatórios — tudo online.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-display text-2xl font-bold mb-1">Entrar na conta</h2>
          <p className="text-muted-foreground text-sm mb-8">
            Introduza as suas credenciais para continuar.
          </p>

          <label className="block text-sm font-medium mb-2">Email</label>
          <div className="relative mb-4">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@monumental.pt"
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-input border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
            />
          </div>

          <label className="block text-sm font-medium mb-2">Palavra-passe</label>
          <div className="relative mb-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-input border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 mt-2 mb-2">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
}
